import * as React from "react";
import { createApi } from "../../../../utils/createApi";
import { logger } from "../../../../../utils/logger";

/**
 * When a site will next arm on its own (ENG-2632), for the disarm dialogs.
 *
 * SUP-954: a Labour Day holiday override replaced the regular schedule, the
 * customer disarmed at 10:32 expecting the usual 19:00 arm, and nothing
 * re-armed the site until they did it themselves at 22:05. The dialog said
 * only "Site will rearm at the next scheduled arm time".
 *
 * `GET customer/{id}/next_arm/` (admin #3207) computes it from the deployed
 * start jobs. An admin without the endpoint (404), no access (403) or any
 * other failure leaves the dialog exactly as before.
 */

export interface NextArmFlexSchedule {
  supported?: boolean | null;
  flex_schedule_id: number;
  name: string | null;
  next_arm_at: string | null;
  source: string | null;
  note: string | null;
}

export interface NextArm {
  /**
   * Whether admin can compute this site's next arm. false for fleet sites,
   * whose schedules live in the fleet worker, not django-q (`supported: false`
   * or `source: "unsupported"`); null when admin doesn't say (older admin).
   */
  supported: boolean | null;
  /** Site-local ISO with offset; null = no automatic arm within horizon_days. */
  next_arm_at: string | null;
  timezone: string | null;
  source: string | null;
  note: string | null;
  horizon_days: number;
  flex_schedules: NextArmFlexSchedule[];
}

/** "Tue, Oct 27, 9:00 PM NZDT" in the site's timezone. */
export const formatNextArm = (
  iso: string,
  timezone?: string | null
): string => {
  const date = new Date(iso);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  try {
    return date.toLocaleString("en-US", {
      ...options,
      timeZone: timezone || undefined,
    });
  } catch {
    return date.toLocaleString("en-US", options);
  }
};

/**
 * The line for one next-arm value, or null to keep the dialog as it was:
 * - not supported (fleet site, or admin says so): nothing, since a null time
 *   there would falsely read as "won't re-arm";
 * - a time: when it next arms;
 * - no time: "won't re-arm" only when admin explicitly says it's supported.
 */
export const nextArmLine = (
  nextArmAt: string | null,
  supported: boolean | null,
  timezone: string | null,
  horizonDays: number,
  subject = "This site"
): string | null => {
  if (supported === false) return null;
  if (nextArmAt)
    return nextArmSentence(nextArmAt, timezone, horizonDays, subject);
  return supported === true
    ? nextArmSentence(null, timezone, horizonDays, subject)
    : null;
};

const supportedFrom = (data: {
  supported?: unknown;
  source?: unknown;
}): boolean | null => {
  if (data.source === "unsupported") return false;
  return typeof data.supported === "boolean" ? data.supported : null;
};

/** The sentence for one next-arm value. */
export const nextArmSentence = (
  nextArmAt: string | null,
  timezone: string | null,
  horizonDays: number,
  subject = "This site"
): string =>
  nextArmAt
    ? `${subject} will next arm automatically at ${formatNextArm(
        nextArmAt,
        timezone
      )}.`
    : `${subject} won't re-arm automatically within the next ${horizonDays} days. Arm it manually when you're ready.`;

export const fetchNextArm = async (
  siteId: string | number
): Promise<NextArm> => {
  const response = await createApi(``).get(`customer/${siteId}/next_arm/`);
  const data = response.data ?? {};
  return {
    supported: supportedFrom(data),
    next_arm_at: data.next_arm_at ?? null,
    timezone: data.timezone ?? null,
    source: data.source ?? null,
    note: data.note ?? null,
    horizon_days: Number(data.horizon_days) || 7,
    flex_schedules: Array.isArray(data.flex_schedules)
      ? data.flex_schedules.map((row: NextArmFlexSchedule) => ({
          ...row,
          supported: supportedFrom(row),
        }))
      : [],
  };
};

/**
 * Loads next_arm while `open` is true. Returns null until it arrives and on
 * any failure, so callers fall back to today's text.
 */
export const useNextArm = (
  siteId: string | number | undefined,
  open: boolean
): NextArm | null => {
  const [nextArm, setNextArm] = React.useState<NextArm | null>(null);
  React.useEffect(() => {
    if (!open || !siteId) {
      setNextArm(null);
      return undefined;
    }
    let cancelled = false;
    fetchNextArm(siteId)
      .then((data) => {
        if (!cancelled) setNextArm(data);
      })
      .catch((error) => {
        // 404 on an admin without the endpoint, 403 without access: keep the
        // dialog as it was.
        logger.warn(
          "next_arm lookup failed; disarm dialog keeps its old text",
          error
        );
        if (!cancelled) setNextArm(null);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, open]);
  return nextArm;
};
